import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import { CreateRecipeInput, Ingredient, Instruction, Recipe, RecipeStatus } from 'Types/api';
import { uploadService } from 'Services/uploadService';
import { useAutoSave, AutoSaveStatus } from 'Hooks/useAutoSave';
import { normalizeInstructions } from 'Utils/instructionUtils';
import { ROUTES } from 'Utils/routes';

const EMPTY_INGREDIENT: Ingredient = { name: '', amount: '', unit: '' };

// Sortable ingredient row component
interface SortableIngredientProps {
  id: string;
  ingredient: Ingredient;
  index: number;
  isSubmitting: boolean;
  canDelete: boolean;
  onUpdate: (index: number, field: keyof Ingredient, value: string) => void;
  onSetToTaste: (index: number) => void;
  onRemove: (index: number) => void;
}

const SortableIngredient: React.FC<SortableIngredientProps> = ({
  id,
  ingredient,
  index,
  isSubmitting,
  canDelete,
  onUpdate,
  onSetToTaste,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Grid
      container
      spacing={1}
      ref={setNodeRef}
      style={style}
      sx={{ mb: 1, alignItems: 'center', bgcolor: isDragging ? 'action.hover' : 'transparent' }}
    >
      <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
        <IconButton
          size='small'
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab', touchAction: 'none' }}
          disabled={isSubmitting}
        >
          <DragIndicatorIcon fontSize='small' />
        </IconButton>
      </Grid>
      <Grid item xs={11} sm={4}>
        <TextField
          fullWidth
          size='small'
          label='Name'
          value={ingredient.name}
          disabled={isSubmitting}
          onChange={(e) => onUpdate(index, 'name', e.target.value)}
        />
      </Grid>
      <Grid item xs={5} sm={3}>
        <TextField
          fullWidth
          size='small'
          label='Amount'
          value={ingredient.amount}
          disabled={isSubmitting}
          onChange={(e) => onUpdate(index, 'amount', e.target.value)}
        />
      </Grid>
      <Grid item xs={4} sm={2}>
        <TextField
          fullWidth
          size='small'
          label='Unit'
          value={ingredient.unit}
          disabled={isSubmitting}
          onChange={(e) => onUpdate(index, 'unit', e.target.value)}
        />
      </Grid>
      <Grid item xs={2} sm={2} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Tooltip
          title={ingredient.amount === 'to taste' ? 'Clear "to taste"' : 'Set as "to taste"'}
        >
          <Button
            size='small'
            variant={ingredient.amount === 'to taste' ? 'contained' : 'outlined'}
            color={ingredient.amount === 'to taste' ? 'success' : 'inherit'}
            onClick={() => onSetToTaste(index)}
            disabled={isSubmitting}
            sx={{
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              fontSize: '0.7rem',
              fontWeight: ingredient.amount === 'to taste' ? 700 : 400,
              textTransform: 'none',
              borderColor: ingredient.amount === 'to taste' ? 'success.main' : 'divider',
            }}
          >
            {ingredient.amount === 'to taste' ? '✓ taste' : 'taste'}
          </Button>
        </Tooltip>
        <IconButton
          size='small'
          onClick={() => onRemove(index)}
          disabled={isSubmitting || !canDelete}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Grid>
    </Grid>
  );
};

// Sortable instruction row component
interface SortableInstructionProps {
  id: string;
  instruction: Instruction;
  index: number;
  isSubmitting: boolean;
  canDelete: boolean;
  isUploading: boolean;
  onUpdateText: (index: number, text: string) => void;
  onRemove: (index: number) => void;
  onImageSelect: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: (index: number) => void;
  fileInputRef: (el: HTMLInputElement | null) => void;
}

const SortableInstruction: React.FC<SortableInstructionProps> = ({
  id,
  instruction,
  index,
  isSubmitting,
  canDelete,
  isUploading,
  onUpdateText,
  onRemove,
  onImageSelect,
  onRemoveImage,
  fileInputRef,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 2,
        p: 2,
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: isDragging ? 'action.hover' : 'transparent',
      }}
    >
      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <IconButton
            size='small'
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', touchAction: 'none' }}
            disabled={isSubmitting}
          >
            <DragIndicatorIcon fontSize='small' />
          </IconButton>
        </Grid>
        <Grid item xs={9} sm={10}>
          <TextField
            fullWidth
            size='small'
            label={`Step ${index + 1}`}
            value={instruction.text}
            disabled={isSubmitting}
            onChange={(e) => onUpdateText(index, e.target.value)}
          />
        </Grid>
        <Grid item xs={2} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton
            size='small'
            onClick={() => onRemove(index)}
            disabled={isSubmitting || !canDelete}
          >
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Grid>

        {/* Step Image Upload */}
        <Grid item xs={12} sx={{ pl: { xs: 0, sm: 6 } }}>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/jpeg,image/png,image/gif,image/webp'
            style={{ display: 'none' }}
            onChange={(e) => onImageSelect(index, e)}
            disabled={isSubmitting || isUploading}
          />
          {instruction.imageUrl ? (
            <Box sx={{ mt: 1 }}>
              <Box
                component='img'
                src={instruction.imageUrl}
                alt={`Step ${index + 1}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 150,
                  objectFit: 'cover',
                  borderRadius: 1,
                  mb: 1,
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = (e.currentTarget as HTMLElement)
                      .closest('.MuiGrid-root')
                      ?.querySelector('input[type="file"]') as HTMLInputElement;
                    input?.click();
                  }}
                  disabled={isSubmitting || isUploading}
                  startIcon={isUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                >
                  {isUploading ? 'Uploading...' : 'Change'}
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  color='error'
                  onClick={() => onRemoveImage(index)}
                  disabled={isSubmitting || isUploading}
                  startIcon={<DeleteIcon />}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              size='small'
              variant='text'
              onClick={(e) => {
                e.stopPropagation();
                const input = (e.currentTarget as HTMLElement)
                  .closest('.MuiGrid-root')
                  ?.querySelector('input[type="file"]') as HTMLInputElement;
                input?.click();
              }}
              disabled={isSubmitting || isUploading}
              startIcon={isUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
              sx={{ mt: 1 }}
            >
              {isUploading ? 'Uploading...' : 'Add Photo'}
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

interface RecipeFormProps {
  initialData?: Recipe;
  onSubmit: (input: CreateRecipeInput) => Promise<void>;
  submitLabel: string;
  submittingLabel: string;
  onAutoSave?: (input: CreateRecipeInput) => Promise<void>;
  autoSaveEnabled?: boolean;
  onImageUploaded?: (input: CreateRecipeInput) => Promise<void>;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Auto-save status indicator component
const AutoSaveIndicator: React.FC<{ status: AutoSaveStatus; lastSavedAt: Date | null }> = ({
  status,
  lastSavedAt,
}) => {
  const getStatusDisplay = () => {
    switch (status) {
      case 'saving':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <CircularProgress size={14} color='inherit' />
            <Typography variant='caption'>Saving...</Typography>
          </Box>
        );
      case 'saved':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            <Typography variant='caption'>
              Saved {lastSavedAt ? formatTimeAgo(lastSavedAt) : ''}
            </Typography>
          </Box>
        );
      case 'error':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
            <ErrorOutlineIcon sx={{ fontSize: 16 }} />
            <Typography variant='caption'>Auto-save failed</Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return <Box sx={{ minHeight: 24 }}>{getStatusDisplay()}</Box>;
};

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

const RecipeForm = ({
  initialData,
  onSubmit,
  submitLabel,
  submittingLabel,
  onAutoSave,
  autoSaveEnabled = false,
  onImageUploaded,
}: RecipeFormProps) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [category, setCategory] = useState(initialData?.category || '');
  const [prepTime, setPrepTime] = useState(initialData ? String(initialData.prepTime) : '');
  const [cookTime, setCookTime] = useState(initialData ? String(initialData.cookTime) : '');
  const [servings, setServings] = useState(initialData ? String(initialData.servings) : '');
  const [imageUrl, setImageUrl] = useState(initialData?.imageUrl || '');
  const [imagePreview, setImagePreview] = useState<string | null>(initialData?.imageUrl || null);
  const [status, setStatus] = useState<RecipeStatus>(initialData?.status || 'draft');
  const [ingredients, setIngredients] = useState<Ingredient[]>(
    initialData?.ingredients.length ? initialData.ingredients : [{ ...EMPTY_INGREDIENT }],
  );
  const [instructions, setInstructions] = useState<Instruction[]>(
    initialData?.instructions.length
      ? normalizeInstructions(initialData.instructions)
      : [{ text: '' }],
  );
  const [instructionUploading, setInstructionUploading] = useState<Record<number, boolean>>({});
  const instructionFileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // Memoize form data for auto-save
  const formData = useMemo((): CreateRecipeInput => {
    return {
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      prepTime: Number(prepTime) || 0,
      cookTime: Number(cookTime) || 0,
      servings: Number(servings) || 1,
      ingredients: ingredients.filter((i) => i.name.trim()),
      instructions: instructions
        .filter((i) => i.text.trim())
        .map((i) => ({ text: i.text.trim(), ...(i.imageUrl && { imageUrl: i.imageUrl }) })),
      status,
      ...(imageUrl.trim() && { imageUrl: imageUrl.trim() }),
    };
  }, [
    title,
    description,
    category,
    prepTime,
    cookTime,
    servings,
    ingredients,
    instructions,
    status,
    imageUrl,
  ]);

  // Check if form has all required fields for auto-save (must pass backend validation)
  const hasRequiredFields = useMemo(() => {
    const hasTitle = title.trim().length > 0;
    const hasDescription = description.trim().length > 0;
    const hasCategory = category.trim().length > 0;
    const hasValidPrepTime = prepTime !== '' && Number(prepTime) >= 0;
    const hasValidCookTime = cookTime !== '' && Number(cookTime) >= 0;
    const hasValidServings = servings !== '' && Number(servings) >= 1;
    const hasValidIngredient = ingredients.some((i) => i.name.trim());
    const hasValidInstruction = instructions.some((i) => i.text.trim());

    return (
      hasTitle &&
      hasDescription &&
      hasCategory &&
      hasValidPrepTime &&
      hasValidCookTime &&
      hasValidServings &&
      hasValidIngredient &&
      hasValidInstruction
    );
  }, [title, description, category, prepTime, cookTime, servings, ingredients, instructions]);

  // Auto-save handler
  const handleAutoSave = useCallback(
    async (data: CreateRecipeInput) => {
      if (onAutoSave && hasRequiredFields) {
        await onAutoSave(data);
      }
    },
    [onAutoSave, hasRequiredFields],
  );

  // Use auto-save hook
  const { status: autoSaveStatus, lastSavedAt } = useAutoSave({
    data: formData,
    onSave: handleAutoSave,
    debounceMs: 2000,
    enabled: autoSaveEnabled && !!onAutoSave && hasRequiredFields,
  });

  // Get form data with placeholder defaults for required fields (used when saving after image upload)
  const getFormDataWithDefaults = useCallback(
    (newImageUrl?: string, newInstructions?: Instruction[]): CreateRecipeInput => {
      const currentInstructions = newInstructions || instructions;
      const filteredIngredients = ingredients.filter((i) => i.name.trim());
      const filteredInstructions = currentInstructions
        .filter((i) => i.text.trim() || i.imageUrl)
        .map((i) => ({
          text: i.text.trim() || 'Step',
          ...(i.imageUrl && { imageUrl: i.imageUrl }),
        }));

      return {
        title: title.trim() || 'Untitled Recipe',
        description: description.trim() || 'No description',
        category: category.trim() || 'Uncategorized',
        prepTime: Number(prepTime) || 0,
        cookTime: Number(cookTime) || 0,
        servings: Number(servings) || 1,
        ingredients:
          filteredIngredients.length > 0
            ? filteredIngredients
            : [{ name: 'Ingredient', amount: '', unit: '' }],
        instructions: filteredInstructions.length > 0 ? filteredInstructions : [{ text: 'Step 1' }],
        status,
        ...((newImageUrl || imageUrl.trim()) && { imageUrl: newImageUrl || imageUrl.trim() }),
      };
    },
    [
      title,
      description,
      category,
      prepTime,
      cookTime,
      servings,
      ingredients,
      instructions,
      status,
      imageUrl,
    ],
  );

  // Trigger save after image upload to prevent orphaned images
  const handleImageUploadComplete = useCallback(
    async (newImageUrl?: string, newInstructions?: Instruction[]) => {
      if (onImageUploaded) {
        const dataWithDefaults = getFormDataWithDefaults(newImageUrl, newInstructions);
        await onImageUploaded(dataWithDefaults);
      }
    },
    [onImageUploaded, getFormDataWithDefaults],
  );

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    if (!category.trim()) newErrors.category = 'Category is required';
    if (!prepTime || Number(prepTime) < 0) newErrors.prepTime = 'Valid prep time is required';
    if (!cookTime || Number(cookTime) < 0) newErrors.cookTime = 'Valid cook time is required';
    if (!servings || Number(servings) < 1) newErrors.servings = 'Valid servings is required';

    const hasValidIngredient = ingredients.some((i) => i.name.trim());
    if (!hasValidIngredient) newErrors.ingredients = 'At least one ingredient is required';

    const hasValidInstruction = instructions.some((i) => i.text.trim());
    if (!hasValidInstruction) newErrors.instructions = 'At least one instruction is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const input: CreateRecipeInput = {
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      prepTime: Number(prepTime),
      cookTime: Number(cookTime),
      servings: Number(servings),
      ingredients: ingredients.filter((i) => i.name.trim()),
      instructions: instructions
        .filter((i) => i.text.trim())
        .map((i) => ({ text: i.text.trim(), ...(i.imageUrl && { imageUrl: i.imageUrl }) })),
      status,
      ...(imageUrl.trim() && { imageUrl: imageUrl.trim() }),
    };

    setIsSubmitting(true);
    try {
      await onSubmit(input);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateIngredient = (index: number, field: keyof Ingredient, value: string) => {
    setIngredients((prev) => {
      const updated = prev.map((ing, i) => (i === index ? { ...ing, [field]: value } : ing));
      // Auto-add new ingredient when editing last one and it has a name
      const isLastItem = index === prev.length - 1;
      const hasName = field === 'name' ? value.trim() : updated[index].name.trim();
      if (isLastItem && hasName) {
        return [...updated, { ...EMPTY_INGREDIENT }];
      }
      return updated;
    });
  };

  const addIngredient = () => setIngredients((prev) => [...prev, { ...EMPTY_INGREDIENT }]);

  const toggleIngredientToTaste = (index: number) => {
    setIngredients((prev) =>
      prev.map((ing, i) =>
        i === index
          ? { ...ing, amount: ing.amount === 'to taste' ? '' : 'to taste', unit: '' }
          : ing,
      ),
    );
  };

  const removeIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const updateInstructionText = (index: number, text: string) => {
    setInstructions((prev) => {
      const updated = prev.map((inst, i) => (i === index ? { ...inst, text } : inst));
      // Auto-add new instruction when editing last one and it has text
      const isLastItem = index === prev.length - 1;
      if (isLastItem && text.trim()) {
        return [...updated, { text: '' }];
      }
      return updated;
    });
  };

  const updateInstructionImage = (index: number, imageUrl: string | undefined) => {
    setInstructions((prev) => prev.map((inst, i) => (i === index ? { ...inst, imageUrl } : inst)));
  };

  const handleRemoveInstructionImage = (index: number) => {
    const imageUrl = instructions[index]?.imageUrl;
    if (imageUrl) {
      uploadService.deleteImage(imageUrl).catch(() => void 0);
    }
    updateInstructionImage(index, undefined);
  };

  const addInstruction = () => setInstructions((prev) => [...prev, { text: '' }]);

  const removeInstruction = (index: number) => {
    // Delete the instruction's image from S3 if it exists
    const imageUrl = instructions[index]?.imageUrl;
    if (imageUrl) {
      uploadService.deleteImage(imageUrl).catch(() => void 0);
    }
    setInstructions((prev) => prev.filter((_, i) => i !== index));
    delete instructionFileInputRefs.current[index];
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleIngredientDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setIngredients((items) => {
        const oldIndex = items.findIndex((_, i) => `ingredient-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `ingredient-${i}` === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleInstructionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setInstructions((items) => {
        const oldIndex = items.findIndex((_, i) => `instruction-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `instruction-${i}` === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleInstructionImageSelect = async (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const inputRef = instructionFileInputRefs.current[index];
    if (inputRef) {
      inputRef.value = '';
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    setUploadError(null);
    setInstructionUploading((prev) => ({ ...prev, [index]: true }));

    const oldImageUrl = instructions[index]?.imageUrl;

    try {
      const url = await uploadService.uploadImage(file);
      updateInstructionImage(index, url);

      // Delete old image after successful upload
      if (oldImageUrl) {
        uploadService.deleteImage(oldImageUrl).catch(() => void 0);
      }

      // Trigger save to prevent orphaned image
      const updatedInstructions = instructions.map((inst, i) =>
        i === index ? { ...inst, imageUrl: url } : inst,
      );
      handleImageUploadComplete(undefined, updatedInstructions);
    } catch {
      setUploadError(`Failed to upload image for step ${index + 1}. Please try again.`);
    } finally {
      setInstructionUploading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    const oldImageUrl = imageUrl;

    try {
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to S3
      const url = await uploadService.uploadImage(file);
      setImageUrl(url);

      // Delete old image after successful upload
      if (oldImageUrl) {
        uploadService.deleteImage(oldImageUrl).catch(() => void 0);
      }

      // Trigger save to prevent orphaned image
      handleImageUploadComplete(url);
    } catch {
      setUploadError('Failed to upload image. Please try again.');
      setImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    if (imageUrl) {
      uploadService.deleteImage(imageUrl).catch(() => void 0);
    }
    setImageUrl('');
    setImagePreview(null);
    setUploadError(null);
  };

  return (
    <Box component='form' noValidate onSubmit={handleSubmit}>
      {/* Auto-save indicator */}
      {autoSaveEnabled && onAutoSave && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label='Title'
            value={title}
            error={!!errors.title}
            helperText={errors.title}
            disabled={isSubmitting}
            onChange={(e) => {
              setTitle(e.target.value);
              clearError('title');
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            multiline
            rows={3}
            label='Description'
            value={description}
            error={!!errors.description}
            helperText={errors.description}
            disabled={isSubmitting}
            onChange={(e) => {
              setDescription(e.target.value);
              clearError('description');
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label='Category'
            value={category}
            error={!!errors.category}
            helperText={errors.category}
            disabled={isSubmitting}
            onChange={(e) => {
              setCategory(e.target.value);
              clearError('category');
            }}
          />
        </Grid>

        <Grid item xs={4} sm={2}>
          <TextField
            required
            fullWidth
            type='number'
            label='Prep (min)'
            value={prepTime}
            error={!!errors.prepTime}
            helperText={errors.prepTime}
            disabled={isSubmitting}
            inputProps={{ min: 0 }}
            onChange={(e) => {
              setPrepTime(e.target.value);
              clearError('prepTime');
            }}
          />
        </Grid>

        <Grid item xs={4} sm={2}>
          <TextField
            required
            fullWidth
            type='number'
            label='Cook (min)'
            value={cookTime}
            error={!!errors.cookTime}
            helperText={errors.cookTime}
            disabled={isSubmitting}
            inputProps={{ min: 0 }}
            onChange={(e) => {
              setCookTime(e.target.value);
              clearError('cookTime');
            }}
          />
        </Grid>

        <Grid item xs={4} sm={2}>
          <TextField
            required
            fullWidth
            type='number'
            label='Servings'
            value={servings}
            error={!!errors.servings}
            helperText={errors.servings}
            disabled={isSubmitting}
            inputProps={{ min: 1 }}
            onChange={(e) => {
              setServings(e.target.value);
              clearError('servings');
            }}
          />
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Image (optional)
          </Typography>
          {uploadError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <input
            ref={fileInputRef}
            type='file'
            accept='image/jpeg,image/png,image/gif,image/webp'
            style={{ display: 'none' }}
            onChange={handleImageSelect}
            disabled={isSubmitting || isUploading}
          />
          {imagePreview ? (
            <Box sx={{ mb: 2 }}>
              <Box
                component='img'
                src={imagePreview}
                alt='Recipe preview'
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 1,
                  mb: 1,
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting || isUploading}
                  startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                >
                  {isUploading ? 'Uploading...' : 'Change Image'}
                </Button>
                <Button
                  size='small'
                  variant='outlined'
                  color='error'
                  onClick={handleRemoveImage}
                  disabled={isSubmitting || isUploading}
                  startIcon={<DeleteIcon />}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              variant='outlined'
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || isUploading}
              startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              sx={{ mb: 2 }}
            >
              {isUploading ? 'Uploading...' : 'Upload Image'}
            </Button>
          )}
          <Typography variant='caption' color='text.secondary' display='block'>
            Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB.
          </Typography>
        </Grid>

        {/* Ingredients */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Ingredients
          </Typography>
          {errors.ingredients && (
            <Typography variant='body2' color='error' sx={{ mb: 1 }}>
              {errors.ingredients}
            </Typography>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleIngredientDragEnd}
          >
            <SortableContext
              items={ingredients.map((_, i) => `ingredient-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              {ingredients.map((ingredient, index) => (
                <SortableIngredient
                  key={`ingredient-${index}`}
                  id={`ingredient-${index}`}
                  ingredient={ingredient}
                  index={index}
                  isSubmitting={isSubmitting}
                  canDelete={ingredients.length > 1}
                  onUpdate={(i, field, value) => {
                    updateIngredient(i, field, value);
                    if (field === 'name') clearError('ingredients');
                  }}
                  onSetToTaste={toggleIngredientToTaste}
                  onRemove={removeIngredient}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button
            size='small'
            startIcon={<AddIcon />}
            onClick={addIngredient}
            disabled={isSubmitting}
          >
            Add Ingredient
          </Button>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Instructions
          </Typography>
          {errors.instructions && (
            <Typography variant='body2' color='error' sx={{ mb: 1 }}>
              {errors.instructions}
            </Typography>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleInstructionDragEnd}
          >
            <SortableContext
              items={instructions.map((_, i) => `instruction-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              {instructions.map((instruction, index) => (
                <SortableInstruction
                  key={`instruction-${index}`}
                  id={`instruction-${index}`}
                  instruction={instruction}
                  index={index}
                  isSubmitting={isSubmitting}
                  canDelete={instructions.length > 1}
                  isUploading={instructionUploading[index] || false}
                  onUpdateText={(i, text) => {
                    updateInstructionText(i, text);
                    clearError('instructions');
                  }}
                  onRemove={removeInstruction}
                  onImageSelect={handleInstructionImageSelect}
                  onRemoveImage={handleRemoveInstructionImage}
                  fileInputRef={(el) => {
                    instructionFileInputRefs.current[index] = el;
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button
            size='small'
            startIcon={<AddIcon />}
            onClick={addInstruction}
            disabled={isSubmitting}
          >
            Add Step
          </Button>
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: status === 'published' ? 'success.50' : 'grey.100',
              border: '1px solid',
              borderColor: status === 'published' ? 'success.200' : 'grey.300',
            }}
          >
            <Typography variant='subtitle2' sx={{ mb: 1.5, fontWeight: 600 }}>
              Visibility
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={status === 'draft' ? 'contained' : 'outlined'}
                color={status === 'draft' ? 'inherit' : 'inherit'}
                size='small'
                startIcon={<LockIcon />}
                onClick={() => setStatus('draft')}
                disabled={isSubmitting}
                sx={{
                  flex: 1,
                  bgcolor: status === 'draft' ? 'grey.700' : 'transparent',
                  color: status === 'draft' ? 'white' : 'text.secondary',
                  borderColor: 'grey.400',
                  '&:hover': {
                    bgcolor: status === 'draft' ? 'grey.800' : 'grey.200',
                  },
                }}
              >
                Draft
              </Button>
              <Button
                variant={status === 'published' ? 'contained' : 'outlined'}
                color='success'
                size='small'
                startIcon={<PublicIcon />}
                onClick={() => setStatus('published')}
                disabled={isSubmitting}
                sx={{
                  flex: 1,
                  borderColor: status === 'published' ? 'success.main' : 'grey.400',
                  color: status === 'published' ? 'white' : 'text.secondary',
                  '&:hover': {
                    bgcolor: status === 'published' ? 'success.dark' : 'success.50',
                  },
                }}
              >
                Publish
              </Button>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
              {status === 'draft' ? (
                <>
                  <LockIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Only you can see this recipe
                </>
              ) : (
                <>
                  <PublicIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  This recipe is visible to everyone
                </>
              )}
            </Typography>
          </Box>
        </Grid>

        {/* Submit */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
            >
              {isSubmitting ? submittingLabel : submitLabel}
            </Button>
            <Button
              variant='outlined'
              onClick={() => navigate(ROUTES.DASHBOARD)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecipeForm;
